/**
 * ETA date helpers. ETAs are calendar dates (YYYY-MM-DD) with no time zone,
 * so comparisons use the viewer's local calendar day.
 */

import {
  ActionItemRecord,
  ActionItemStatus,
} from 'src/types/actionItems.types';

/** Statuses that end an item; a past ETA on these is not overdue. */
const CLOSED: ReadonlySet<ActionItemStatus> = new Set<ActionItemStatus>([
  'DONE',
  'CANCELLED',
]);

/** Today as a local YYYY-MM-DD string, directly comparable to an ETA. */
export function todayIsoDate(now: Date = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Local date N days from now, as YYYY-MM-DD. */
function plusDays(now: Date, days: number): string {
  const d = new Date(now);
  d.setDate(d.getDate() + days);
  return todayIsoDate(d);
}

/** Open item whose ETA is before today. */
export function isOverdue(
  item: ActionItemRecord,
  now: Date = new Date(),
): boolean {
  return !!item.eta && !CLOSED.has(item.status) && item.eta < todayIsoDate(now);
}

export type EtaBucket =
  | 'OVERDUE'
  | 'TODAY'
  | 'THIS_WEEK'
  | 'NEXT_WEEK'
  | 'LATER';

export const ETA_BUCKET_LABELS: Record<EtaBucket, string> = {
  OVERDUE: 'Overdue',
  TODAY: 'Due today',
  THIS_WEEK: 'Due this week',
  NEXT_WEEK: 'Due next week',
  LATER: 'Due later',
};

/**
 * Which ETA filter bucket an item falls into; disjoint windows relative to
 * today. Items without an ETA (legacy) match no bucket.
 */
export function etaBucket(
  item: ActionItemRecord,
  now: Date = new Date(),
): EtaBucket | null {
  if (!item.eta) return null;
  const today = todayIsoDate(now);
  // Past-date: overdue if still open; a closed item is simply done.
  if (item.eta < today) return CLOSED.has(item.status) ? null : 'OVERDUE';
  if (item.eta === today) return 'TODAY';
  if (item.eta <= plusDays(now, 6)) return 'THIS_WEEK';
  if (item.eta <= plusDays(now, 13)) return 'NEXT_WEEK';
  return 'LATER';
}
